import { Dropdown, IColumn, IDropdownOption, MessageBarType, Text } from '@fluentui/react';
import { FC, ReactElement, useEffect, useState } from 'react';
import { sortOrder } from '../../../../../../../../../consts/sortOrder';
import useNotifications from '../../../../../../../../../hooks/useNotifications';
import { IPaginationProps } from '../../../../../../../../Pagination/IPaginationProps';
import { vehicleMileageColumns } from './consts';
import { pageSizes } from '../../../../../../../../../consts/recordKeeping';
import { IVehicleMileageProps } from './IVehicleMileageProps';
import { IVehicleMileageState } from './IVehicleMileageState';
import apiService from '../../../../../../../../../api';
import styles from './VehicleMileage.module.scss';
import SeparatorGy from '../../../../../../../../SeparatorGy/SeparatorGy';
import Pagination from '../../../../../../../../Pagination/Pagination';
import classNames from 'classnames';
import LoadingScreen from '../../../../../../../../LoadingScreen/LoadingScreen';

const VehicleMileage: FC<IVehicleMileageProps> = ({ 
  billPeriodId, 
  setViewReportVehiclePaginationProps,
  setReportVehicleSortOrder,
  setReportVehicleSortOrderCountOnPage,
}): ReactElement => {

  const onColumnClick = (column: IColumn): void => {
    const newColumns: IColumn[] = [...columnsState];
    const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });

    setColumnsState(newColumns);
  };

  const { addNotification } = useNotifications();

  const [state, setState] = useState<IVehicleMileageState>({
    items: [],
    loading: false,
    foundCount: 0,
  });

  const [paginationProps, setPaginationProps] = useState<IPaginationProps>({
    total: 0,
    current: 1,
    onChangePage: (newPage: number) => {
      setPaginationProps((prev: any) => ({ ...prev, current: newPage }));
    },
  });
  const [countOnPage, setCountOnPage] = useState<IDropdownOption>({ key: pageSizes[0], text: pageSizes[0].toString() });
  const [columnsState, setColumnsState] = useState<Array<any>>(vehicleMileageColumns);

  const onChangeCountOnPage = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption<any> | undefined): void => {
    setPaginationProps((prev: any) => ({ ...prev, current: 1 }));
    item && setCountOnPage(item);
  };

  const getSortOrder = () => {
    const { fieldName, isSortedDescending } = columnsState.find(({ isSorted }) => isSorted);
    return {
      column: fieldName,
      order: isSortedDescending ? sortOrder.DESC : sortOrder.ASC,
    };
  };

  const fetchVehicleMileageReport = async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const sortOrder = getSortOrder();
      const { data }: any = await apiService.mileageRun.getVehiclesMileageReport(billPeriodId,
        { pageNumber: paginationProps.current, pageSize: +countOnPage.key },
        sortOrder,
      );
      const foundCount = data.total.found;
      const items = data.data;
      setState((prev: any) => ({ ...prev, items, foundCount, selectedItems: [] }));
      setPaginationProps((prev: any) => ({ ...prev, total: Math.ceil(foundCount / +countOnPage.key) }));
      setViewReportVehiclePaginationProps((prev: any) => ({ ...prev, total: Math.ceil(foundCount / +countOnPage.key) }));
      setReportVehicleSortOrder(sortOrder);
      setReportVehicleSortOrderCountOnPage(countOnPage);
    } catch (e: any) {
      addNotification({
        text: `Vehicle Mileage Report fetching error: ${e?.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setState((prev: any) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchVehicleMileageReport();
  }, [paginationProps.current, countOnPage, columnsState, billPeriodId]);

  return (
    <div>
      <div className={styles.tableHeading}>
        <div>
          <Text variant="xLarge" className={styles.highlight}>Vehicle Mileage</Text>
          <SeparatorGy vertical />
          <Text variant="xLarge" className={styles.highlight}>{state.foundCount} found</Text>
        </div>
        <div>
          <Text variant="large" className={styles.highlight}>Show # of rows:&nbsp;</Text>
          <Dropdown
            options={pageSizes.map(pageSize => ({
              key: pageSize,
              text: pageSize.toString(),
            }))}
            defaultSelectedKey={pageSizes[0]}
            selectedKey={countOnPage?.key}
            onChange={onChangeCountOnPage}
          />
        </div>
      </div>
      <div className={styles['table-wrapper']}>
        <table className={styles.table}>
          <thead>
            <tr>
              {
                columnsState.map(item => (
                  <th
                    key={item.name}
                    className={classNames(
                      item.isSorted && item.isSortedDescending ?
                        styles.descending : item.isSorted && !item.isSortedDescending ? styles.ascending : undefined,
                      styles[item.key],
                    )}
                    onClick={() => onColumnClick(item)}>{item.name}
                  </th>
                ))
              }
            </tr>
          </thead>
          <tbody>
            {state.items.map((item, i) => (
              <tr key={i} className={styles.trBasic} >
                <td>{item.vno}</td>
                <td>{item.rptUnits}</td>
                <td>{item.gdyrUnits}</td>
                <td>{item.miscUnits}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SeparatorGy />
      <Pagination {...paginationProps} />
      {state.loading && <LoadingScreen />}
    </div> 
  );
};

export default VehicleMileage;